// Error envelope (spec §11). Messages are safe for users; technical detail goes to sanitized logs only.
import { z } from 'zod';

export const ErrorCode = z.enum([
  // required by spec §11
  'AUTH_INVALID', 'ACCOUNT_EXPIRED', 'ACCOUNT_DISABLED', 'PROVIDER_UNREACHABLE', 'PROVIDER_RATE_LIMITED',
  'MALFORMED_RESPONSE', 'GUIDE_UNAVAILABLE', 'ARCHIVE_UNAVAILABLE', 'UNSUPPORTED_MEDIA', 'CONNECTION_LIMIT',
  'RESOURCE_FORBIDDEN', 'SESSION_EXPIRED', 'STORAGE_UNAVAILABLE', 'CONVERSION_CAPACITY',
  // ADR-0004 client-side conversion
  'CONVERSION_FAILED',
  // application additions (ADR-0004)
  'VALIDATION_FAILED', 'INSECURE_TRANSPORT_UNCONFIRMED', 'ENDPOINT_MISSING', 'CSRF_REJECTED',
  'UNAUTHORIZED', 'NOT_FOUND', 'RATE_LIMITED', 'CURSOR_EXPIRED', 'CONFLICT', 'PIN_REQUIRED',
  'EGRESS_BLOCKED', 'INTERNAL',
]);
export type ErrorCode = z.infer<typeof ErrorCode>;

export const ApiError = z.object({
  error: z.object({
    code: ErrorCode,
    message: z.string().max(500).describe('Safe user message with a next action'),
    retryable: z.boolean(),
    requestId: z.string().max(64),
    details: z.record(z.string(), z.unknown()).optional().describe('Safe structured details; never secrets'),
  }),
});
export type ApiError = z.infer<typeof ApiError>;

/** Default HTTP status per code. Handlers may override when a better status applies. */
export const errorStatus: Record<ErrorCode, number> = {
  AUTH_INVALID: 401, ACCOUNT_EXPIRED: 403, ACCOUNT_DISABLED: 403, PROVIDER_UNREACHABLE: 502,
  PROVIDER_RATE_LIMITED: 429, MALFORMED_RESPONSE: 502, GUIDE_UNAVAILABLE: 404, ARCHIVE_UNAVAILABLE: 404,
  UNSUPPORTED_MEDIA: 415, CONNECTION_LIMIT: 409, RESOURCE_FORBIDDEN: 403, SESSION_EXPIRED: 401,
  STORAGE_UNAVAILABLE: 503, CONVERSION_CAPACITY: 503, CONVERSION_FAILED: 422, VALIDATION_FAILED: 400,
  INSECURE_TRANSPORT_UNCONFIRMED: 400, ENDPOINT_MISSING: 502, CSRF_REJECTED: 403, UNAUTHORIZED: 401,
  NOT_FOUND: 404, RATE_LIMITED: 429, CURSOR_EXPIRED: 410, CONFLICT: 409, PIN_REQUIRED: 403,
  EGRESS_BLOCKED: 403, INTERNAL: 500,
};

/** Default safe user messages (each gives a next action). */
export const errorMessage: Record<ErrorCode, string> = {
  AUTH_INVALID: 'The server rejected that username or password. Check them and try again.',
  ACCOUNT_EXPIRED: 'This provider account has expired. Renew it with your provider, then reconnect.',
  ACCOUNT_DISABLED: 'This provider account is disabled. Contact your provider, then reconnect.',
  PROVIDER_UNREACHABLE: 'Tuned could not reach your provider. Check the server address or try again shortly.',
  PROVIDER_RATE_LIMITED: 'Your provider is limiting requests. Tuned will retry automatically; try again in a minute.',
  MALFORMED_RESPONSE: 'Your provider sent a response Tuned could not read. Try again, or open Diagnostics.',
  GUIDE_UNAVAILABLE: 'Schedule unavailable for this channel. You can still watch it live.',
  ARCHIVE_UNAVAILABLE: 'This programme is not available to replay. Watch live instead.',
  UNSUPPORTED_MEDIA: 'This stream uses a format your browser cannot play. See Diagnostics for details.',
  CONNECTION_LIMIT: 'All of your provider connections are in use. Stop another stream to watch this one.',
  RESOURCE_FORBIDDEN: 'You do not have access to this item.',
  SESSION_EXPIRED: 'Your provider session expired. Reconnect to keep watching.',
  STORAGE_UNAVAILABLE: 'Saving is temporarily unavailable. Your changes will retry automatically.',
  CONVERSION_CAPACITY: 'Format conversion is busy right now. Try again in a moment.',
  CONVERSION_FAILED: "Tuned couldn't convert this title in your browser. Try another title, or Safari.",
  VALIDATION_FAILED: 'Some details need attention. Check the highlighted fields.',
  INSECURE_TRANSPORT_UNCONFIRMED: 'This server uses unencrypted HTTP. Confirm you understand before connecting.',
  ENDPOINT_MISSING: 'Your provider does not offer this feature.',
  CSRF_REJECTED: 'Your request could not be verified. Reload the page and try again.',
  UNAUTHORIZED: 'This device is not signed in. Connect a provider to continue.',
  NOT_FOUND: 'That item is no longer available.',
  RATE_LIMITED: 'Too many attempts. Wait a minute and try again.',
  CURSOR_EXPIRED: 'The catalog was updated. Reloading the list from the start.',
  CONFLICT: 'This changed somewhere else. Reload to see the latest version.',
  PIN_REQUIRED: 'Enter the profile PIN to open this content.',
  EGRESS_BLOCKED: 'Tuned blocked a request to a disallowed address for your safety.',
  INTERNAL: 'Something went wrong on our side. Try again.',
};

export const retryableCodes: ReadonlySet<ErrorCode> = new Set<ErrorCode>([
  'PROVIDER_UNREACHABLE', 'PROVIDER_RATE_LIMITED', 'STORAGE_UNAVAILABLE', 'CONVERSION_CAPACITY', 'RATE_LIMITED', 'INTERNAL',
]);
